import React, { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { toast } from "sonner";
import { Clock, Hand, Pencil } from "lucide-react";
import { api, errMsg } from "@/lib/api";
import { Pill, Loader } from "@/components/Bits";
import { useAuth } from "@/context/AuthContext";

const FALLBACK =
  "https://images.unsplash.com/photo-1517457373958-b7bdd4587205?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxODd8MHwxfHNlYXJjaHwzfHxjb21tdW5pdHklMjBnYXRoZXJpbmclMjBwZW9wbGUlMjBjb25uZWN0aW5nfGVufDB8fHx8MTc4NjIwNDkxNHww&ixlib=rb-4.1.0&q=85";

const BlogDetail = () => {
  const { id } = useParams();
  const { user, login } = useAuth();
  const [b, setB] = useState<any>(null);

  useEffect(() => {
    api.get(`/blogs/${id}`).then((r) => setB(r.data)).catch(() => setB(false));
  }, [id]);

  const clap = async () => {
    if (!user) return login();
    try {
      const { data } = await api.post(`/blogs/${id}/clap`);
      setB({ ...b, claps: data.claps });
      toast.success("Thanks for backing this piece.");
    } catch (e) {
      toast.error(errMsg(e));
    }
  };

  if (b === null) return <Loader />;
  if (b === false) return <div className="mx-auto max-w-[1400px] px-5 py-32 text-white/50" data-testid="blog-not-found">Blog not found.</div>;

  return (
    <div data-testid="blog-detail-page">
      <div className="relative h-[42vh] min-h-[300px] overflow-hidden border-b border-white/10">
        <img src={b.cover_image || FALLBACK} alt={b.title} className="h-full w-full object-cover opacity-35" />
        <div className="absolute inset-0 bg-gradient-to-t from-ink-base via-ink-base/60 to-transparent" />
        <div className="absolute bottom-0 left-0 right-0">
          <div className="mx-auto max-w-3xl px-5 lg:px-10 pb-10">
            <div className="flex gap-2">
              <Pill tone="blue">{b.category}</Pill>
              {b.status !== "approved" && <Pill tone="yellow">{b.status}</Pill>}
            </div>
            <div className="mt-5 flex flex-wrap items-start justify-between gap-4">
              <h1 className="font-bebas text-5xl sm:text-6xl tracking-wide uppercase leading-[0.92]" data-testid="blog-title">{b.title}</h1>
              {user && (user.user_id === b.author_id || user.role === "admin") && (
                <Link to={`/blogs/${id}/edit`} data-testid="blog-edit-link"
                  className="flex items-center gap-2 border border-white/20 px-5 h-10 text-[12px] font-bold uppercase tracking-[0.18em] hover:border-neon hover:text-neon transition-colors">
                  <Pencil size={14} /> Edit
                </Link>
              )}
            </div>
            <div className="mt-5 flex flex-wrap items-center gap-5 text-[11px] uppercase tracking-[0.18em] text-white/50">
              <span>{b.author_name}</span>
              <span className="flex items-center gap-1.5"><Clock size={12} /> {b.read_minutes} min read</span>
              <span>{new Date(b.created_at).toLocaleDateString("en-IN", { dateStyle: "medium" })}</span>
            </div>
          </div>
        </div>
      </div>

      <article className="mx-auto max-w-3xl px-5 lg:px-10 py-16">
        <p className="text-xl text-white/75 leading-relaxed">{b.summary}</p>
        <div className="mt-10 whitespace-pre-line text-base text-white/60 leading-[1.9]" data-testid="blog-body">{b.body}</div>

        {b.tags?.length > 0 && (
          <div className="mt-12 flex flex-wrap gap-2">
            {b.tags.map((t: string) => <Pill key={t}>#{t}</Pill>)}
          </div>
        )}

        <div className="mt-14 border-t border-white/10 pt-8 flex items-center gap-5">
          <button onClick={clap} data-testid="clap-btn"
            className="flex items-center gap-2 border border-white/20 px-6 h-12 text-[12px] font-bold uppercase tracking-[0.18em] hover:border-neon hover:text-neon transition-colors">
            <Hand size={16} /> Clap
          </button>
          <span className="font-bebas text-4xl tracking-wide" data-testid="clap-count">{b.claps}</span>
          <span className="text-[11px] uppercase tracking-[0.2em] text-white/35">claps</span>
        </div>
      </article>
    </div>
  );
};

export default BlogDetail;
